import truncatise from 'truncatise'

/**
 * @NB defaults to character count, you can optionally use word count by specifying the
 * third argument as true
 */
export const ellipsify = (
    html: string = '',
    maxLength: number,
    useWordCount?: boolean
) => {
    return truncatise(html, {
        TruncateBy: useWordCount ? 'words' : 'characters',
        TruncateLength: maxLength,
        Strict: false, // means that we do not break strings in the middle of a word
    })
}

// @see https://medium.com/@TCAS3/debounce-deep-dive-javascript-es6-e6f8d983b7a1
export function debounce<T extends any>(
    fn: (...args: T[]) => any,
    time: number
): (...args: T[]) => void {
    let timeout: any
    return function (this: any, ...args: any[]) {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
            fn.apply(this, args)
        }, time)
    }
}

/**
 * Memoize a function that only has one argument (don't use for functions with multiple arguments).
 * Works with object arguments too, not just primitives.
 * 
 * Example Usage:

    const getNumberFormatter = memoizeFirst((locale: string) => new Intl.NumberFormat(locale))

    function localNumberFormat(num: number) {
        const formatter = getNumberFormatter(navigator.languages[0])
        return formatter.format(num)
    }
 */
export const memoizeFirst = (fn: (arg: any) => any) => {
    const objectCache = new WeakMap()
    const primitiveCache: Record<string, string> = {}
    return (arg: any) => {
        if (
            (typeof arg === 'object' && arg !== null) ||
            typeof arg === 'function'
        ) {
            if (!objectCache.has(arg)) {
                const result = fn(arg)
                objectCache.set(arg, result)
                return result
            }
            return objectCache.get(arg)
        }
        if (!primitiveCache.hasOwnProperty(arg)) {
            const result = fn(arg)
            // @NOTE: arg is coerced to a string here
            primitiveCache[arg] = result
            return result
        }
        return primitiveCache[arg]
    }
}

/**
 * prefix first class with `js_`, which is used as hooks
 * for 3rd party code, ie GTM.
 * In case where more than once class get passed in,
 * the method will only prefix the 1st class with `js`
 */
export const buildClassName = (classNames: string) => {
    const [firstClass, ...otherClasses] = classNames.split(' ')
    const jsHook = firstClass.includes('js_') ? firstClass : `js_${firstClass}`
    return `${jsHook} ${otherClasses.join(' ')}`.trim()
}

/**
 * @NB client-side-only
 */
export const preventScroll = (
    shouldPreventScroll = true,
    containerId?: string
) => {
    let container = containerId && document.getElementById(containerId)
    if (!container) {
        container = document.body
    }
    if (shouldPreventScroll) {
        container.style.overflow = 'hidden'
        return
    }
    container.style.overflow = ''
}

/**
 * lightweight method of stripping HTML tags
 * @see https://stackoverflow.com/questions/822452/strip-html-from-text-javascript
 */
export const stripHtmlTags = (html: string = '') => {
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent
}

// forces scrollbar to be visible on OSX and iOS when there is scrollable content
// and `overflow` is set to `auto` or `scroll`
export const visibleScrollbarOnWebkit = (
    backgroundColor: string = 'rgba(0, 0, 0, 0.2)'
) => `
    &::-webkit-scrollbar {
        width: 7px;
    }
    &::-webkit-scrollbar:horizontal {
        height: 7px;
    }
    ::-webkit-scrollbar-thumb {
        background-color: ${backgroundColor};
        border-radius: 4px;
        box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
    }
`

export function isInViewport(element: HTMLElement | null) {
    if (!element) {
        return false
    }
    const rect = element.getBoundingClientRect()
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= document.documentElement.clientHeight &&
        rect.right <= document.documentElement.clientWidth
    )
}
